import { type CSSProperties, useEffect, useState } from 'react';

import { VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import TransitionPortal from '@/components/Common/TransitionPortal';
import { Paragraph, Span } from '@/components/Typography/Typo';
import STORAGE_KEYS from '@/configs/local-storage-keys';
import { InsightExplorerViewType } from '@/features/InsightExplorer/types/constants';
import useColor from '@/hooks/domain/useColor';
import useWorkspaceStorage from '@/hooks/pure/useWorkspaceStorage';
import useInsightExplorerStore from '@/stores/InsightExplorerStore';
import { radius } from '@/styles/sizes';

type Props = {
  style?: CSSProperties;
};

enum GUIDE_STATE {
  SHOW = 1,
  HIDE,
}

const SentimentGuideTooltip = (props: Props) => {
  const color = useColor();
  const { setState } = useInsightExplorerStore();
  const [storageState, setStorageState] = useWorkspaceStorage(
    STORAGE_KEYS.INSIGHT_EXPLORER_SENTIMENT_GUIDE,
    GUIDE_STATE.SHOW,
  );

  const [tempState, setTempState] = useState(storageState);

  useEffect(() => {
    setTempState(storageState);
  }, [storageState]);

  return (
    <TransitionPortal
      style={{
        ...props.style,
        width: 280,
        padding: '20px 24px',
        borderRadius: radius.card,
        border: `1px solid ${color.gray250}`,
        backgroundColor: color.gray900,
        color: color.gray100,
      }}
      layerId="modal-layer"
      status={tempState === GUIDE_STATE.SHOW}
      close={() => setTempState(GUIDE_STATE.HIDE)}
      background={{
        enable: true,
        style: {
          backgroundColor: 'transparent',
        },
      }}
    >
      <VFlex g={18}>
        <VFlex g={10}>
          <Span type="label2" weight="sb">
            Check out the sentiments
          </Span>
          <Paragraph type="body3">
            Identify problematic issues based on the sentiment score or the
            count of negative feedback.
          </Paragraph>
        </VFlex>
        <Button
          variant="outlined"
          onClick={() => {
            setState({ view: InsightExplorerViewType.SENTIMENTS });
            setStorageState(GUIDE_STATE.HIDE);
          }}
        >
          View sentiment
        </Button>
      </VFlex>
    </TransitionPortal>
  );
};

export default SentimentGuideTooltip;
